import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

/**
 * Everything you can do to the thing you are looking at, on one row.
 *
 * Actions used to scatter: some in a page header, some in an "Actions" card
 * halfway down a rail, some behind a row-hover icon, some only reachable from
 * inside a dialog. Supervisor Job Card Detail spread eleven of them across three
 * cards. Nobody can hold that map in their head, so most of the actions were
 * simply never found.
 *
 * One bar, always in the same place, always showing what this record can do.
 */

// Actions render through the shared Button so they keep the app's mnemonic
// handling and focus ring; only the colour weight differs per tone.
const TONES = {
  default: 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
  primary: 'bg-brand-blue text-white shadow-sm hover:bg-brand-blue-dark',
  accent: 'bg-brand-gold text-brand-navy shadow-sm hover:bg-brand-gold-dark',
  good: 'text-emerald-700 hover:bg-emerald-50',
  danger: 'text-red-600 hover:bg-red-50',
};

export function CommandBar({
  title, subtitle, badges, actions = [], onBack, children, className, 'data-testid': testId,
}) {
  return (
    <div
      className={cn(
        'flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 shadow-card',
        className
      )}
      data-testid={testId || 'command-bar'}
    >
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          className="-ml-1 rounded p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 sm:p-1"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
      )}

      {(title || subtitle) && (
        <div className="flex min-w-0 items-baseline gap-2">
          {title && <span className="truncate font-heading text-sm font-bold text-slate-900">{title}</span>}
          {subtitle && <span className="truncate text-xs text-slate-500">{subtitle}</span>}
        </div>
      )}

      {badges && <div className="flex shrink-0 items-center gap-1.5">{badges}</div>}

      {children}

      {actions.length > 0 && (
        <div className="ml-auto flex flex-wrap items-center gap-1">
          {actions.filter(Boolean).map((action) => {
            const Icon = action.icon;
            return (
              <Button
                key={action.key || action.label}
                variant="ghost"
                size="sm"
                mnemonic={action.mnemonic}
                onClick={action.onClick}
                disabled={action.disabled}
                title={action.title || action.label}
                data-testid={action.testId}
                className={cn('h-9 gap-1.5 px-2.5 sm:h-7 sm:px-2 [&_svg]:size-3.5', TONES[action.tone] || TONES.default)}
              >
                {Icon && <Icon />}
                {action.label}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default CommandBar;
